import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

const BulkImportPreview = ({ open, onClose, previewData, onApply, entityType }) => {
  const [selectedNew, setSelectedNew] = useState([]);
  const [selectedUpdates, setSelectedUpdates] = useState([]);
  const [expandedChanges, setExpandedChanges] = useState({});

  // Initialize selections (all selected by default)
  React.useEffect(() => {
    if (previewData) {
      const { new_records, updates } = previewData;
      // Map to row numbers (Excel row numbers starting from 2)
      setSelectedNew(new_records.map((record) => record.row));
      setSelectedUpdates(updates.map((update) => update.row));
      setExpandedChanges({});
    }
  }, [previewData]);

  if (!previewData) return null;

  // Guard against undefined previewData
  if (!previewData) {
    return null;
  }

  const { summary, new_records, updates, errors, existing_records } = previewData;

  const toggleNewSelection = (rowNum) => {
    setSelectedNew(prev =>
      prev.includes(rowNum) ? prev.filter(i => i !== rowNum) : [...prev, rowNum]
    );
  };

  const toggleUpdateSelection = (rowNum) => {
    setSelectedUpdates(prev =>
      prev.includes(rowNum) ? prev.filter(i => i !== rowNum) : [...prev, rowNum]
    );
  };

  const toggleExpandChanges = (idx) => {
    setExpandedChanges(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleApply = () => {
    // Call onApply with two separate parameters (selectedNew, selectedUpdates)
    // Not as an object, since parent components expect (selectedNew, selectedUpdates)
    onApply(selectedNew, selectedUpdates);
  };

  const getFieldLabel = (field) => {
    return field.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Preview - {entityType}</DialogTitle>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">{summary.new_records}</div>
            <div className="text-sm text-green-600">New Records</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{summary.updates_available}</div>
            <div className="text-sm text-blue-600">Updates Available</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-700">{summary.existing_records}</div>
            <div className="text-sm text-gray-600">No Changes</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-700">{summary.errors}</div>
            <div className="text-sm text-red-600">Errors</div>
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Errors
            </h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
              {errors.map((error, idx) => (
                <div key={idx} className="text-sm text-red-700 mb-1">
                  {typeof error === 'string' ? error : `Row ${error.row}: ${error.error}`}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Records */}
        {new_records.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              New Records to Create ({selectedNew.length} selected)
            </h3>
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedNew.length === new_records.length}
                        onChange={(e) => {
                          setSelectedNew(e.target.checked ? new_records.map((record) => record.row) : []);
                        }}
                        className="w-4 h-4"
                      />
                    </TableHead>
                    <TableHead>Row</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {new_records.map((record, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedNew.includes(record.row)}
                          onChange={() => toggleNewSelection(record.row)}
                          className="w-4 h-4"
                        />
                      </TableCell>
                      <TableCell>{record.row}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {record.contact_name && <div><strong>{record.contact_name}</strong></div>}
                          {record.client_number && <div className="text-gray-600">Client: {record.client_number}</div>}
                          {record.data.client_number && <div><strong>{record.data.client_name}</strong></div>}
                          {record.data.email && <div className="text-gray-600">{record.data.email}</div>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Updates */}
        {updates.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-blue-500" />
              Updates Available ({selectedUpdates.length} selected)
            </h3>
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedUpdates.length === updates.length}
                        onChange={(e) => {
                          setSelectedUpdates(e.target.checked ? updates.map((update) => update.row) : []);
                        }}
                        className="w-4 h-4"
                      />
                    </TableHead>
                    <TableHead>Row</TableHead>
                    <TableHead>Record</TableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {updates.map((update, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedUpdates.includes(update.row)}
                          onChange={() => toggleUpdateSelection(update.row)}
                          className="w-4 h-4"
                        />
                      </TableCell>
                      <TableCell>{update.row}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {update.contact_name && <div><strong>{update.contact_name}</strong></div>}
                          {update.client_name && <div><strong>{update.client_name}</strong></div>}
                          {update.client_number && <div className="text-gray-600">Client: {update.client_number}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{Object.keys(update.changes).length} fields</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpandChanges(idx)}
                        >
                          {expandedChanges[idx] ? 'Hide' : 'Show'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {updates.map((update, idx) => (
                    expandedChanges[idx] && (
                      <TableRow key={`expanded-${idx}`}>
                        <TableCell colSpan={5} className="bg-blue-50">
                          <div className="p-4">
                            <h4 className="font-semibold mb-2">Field Changes:</h4>
                            <div className="grid grid-cols-1 gap-2">
                              {Object.entries(update.changes).map(([field, change]) => (
                                <div key={field} className="flex items-center gap-4 text-sm">
                                  <div className="font-medium w-32">{getFieldLabel(field)}:</div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-red-600 line-through">
                                      {String(change.old || 'empty')}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-gray-400" />
                                    <span className="text-green-600 font-semibold">
                                      {String(change.new)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Existing Records (No Changes) */}
        {existing_records.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2 text-gray-600">
              Existing Records (No Changes): {existing_records.length}
            </h3>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={selectedNew.length === 0 && selectedUpdates.length === 0}
          >
            Apply Changes ({selectedNew.length + selectedUpdates.length} items)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportPreview;
